import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  HelpCircle,
  Loader2,
  RefreshCw,
  Trash2,
  MoreVertical,
  CalendarDays,
  FlaskConical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { toast } from "@/hooks/use-toast";
import { ConsolidatedLandscape } from "@/types";
import { landscapeApi } from "@/resources/landscape";
import { useAppData } from "@/contexts/AppDataContext";
import { RoutePath } from "@/routes";
import {
  isLandscapeRunOpenable,
  landscapeRunKeywordCountDisplay,
  runStatusLabel,
  failedLandscapeReason,
} from "@/utils/landscapeRunStatus";
import { PresenceCompare } from "../PresenceCompare";
import { ScheduleLandscapeModal } from "../ScheduleLandscapeModal";
import {
  NestedHistoryTable,
  INITIAL_VISIBLE_VERSIONS,
  SortField,
  NestedSortState,
} from "./HistoryRow";

export interface ConsolidatedLandscapeRowProps {
  landscape: ConsolidatedLandscape;
  animated?: boolean;
  isSelected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onViewInstance?: (instanceId: string) => void;
  heroAsin?: string;
  heroProductId?: number;
  readOnly?: boolean;
  isBulkDeleting?: boolean;
  isPendingDelete?: boolean;
  onCreateAbTest?: (keyword: string) => void;
  onDeleteLandscape: (landscapeId: string) => void;
}

export function ConsolidatedLandscapeRow({
  landscape,
  animated = false,
  isSelected,
  onSelect,
  onViewInstance,
  heroAsin,
  heroProductId,
  readOnly = false,
  isBulkDeleting = false,
  isPendingDelete = false,
  onCreateAbTest,
  onDeleteLandscape,
}: ConsolidatedLandscapeRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_VERSIONS);
  const [nestedSort, setNestedSort] = useState<NestedSortState>({ field: null, direction: null });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const { mutate: createLandscape } = landscapeApi.useCreateLandscape();

  const instances = landscape.instances || [];
  const latestInstance = instances[0];
  const hasHistory = instances.length > 1;
  const historyInstances = instances.slice(1);
  const totalHistory = historyInstances.length;

  if (!latestInstance) return null;

  const isLandscapeOpenable = isLandscapeRunOpenable(latestInstance.status);
  const isLandscapeFailed = latestInstance.status === "failed";
  const isLandscapeStatusUnknown = latestInstance.status === null;
  const statusToastInfo =
    (isLandscapeFailed ? failedLandscapeReason(latestInstance.status_info) : latestInstance.status_info) ||
    "No additional info available.";

  const handleView = (instanceId: string) => {
    if (onViewInstance) {
      onViewInstance(instanceId);
    } else {
      navigate(`${RoutePath.KeywordLandscapes}/${instanceId}`);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
    if (isExpanded) setVisibleCount(INITIAL_VISIBLE_VERSIONS);
  };

  const handleRowClick = () => {
    if (isPendingDelete) return;
    if (isLandscapeOpenable) {
      handleView(latestInstance.id);
    } else {
      toast({
        title: `Status: ${runStatusLabel(latestInstance.status)}`,
        description: statusToastInfo,
      });
    }
  };

  const toggleNestedSort = (field: SortField) => {
    setNestedSort((prev) => {
      if (prev.field !== field) return { field, direction: "desc" };
      if (prev.direction === "desc") return { field, direction: "asc" };
      return { field: null, direction: null };
    });
  };

  const sortedHistory = [...historyInstances].sort((a, b) => {
    if (!nestedSort.field || !nestedSort.direction) return 0;
    const dir = nestedSort.direction === "asc" ? 1 : -1;
    switch (nestedSort.field) {
      case "phrase":
      case "date":
        return dir * (new Date(a.date).getTime() - new Date(b.date).getTime());
      case "top4":
        return dir * (a.top4Hero - b.top4Hero);
      case "page1":
        return dir * (a.page1Hero - b.page1Hero);
      case "keywords":
        return dir * (a.keywordCount - b.keywordCount);
      default:
        return 0;
    }
  });

  const visibleHistory = sortedHistory.slice(0, visibleCount);
  const hasMore = visibleCount < totalHistory;

  return (
    <>
      <TableRow
        className={`hover:bg-muted/20 cursor-pointer ${animated ? "animate-fade-in" : ""} ${isPendingDelete ? "opacity-50 pointer-events-none" : ""}`}
        onClick={handleRowClick}
      >
        <TableCell className="px-3 py-2 w-10" onClick={(e) => e.stopPropagation()}>
          {!readOnly ? (
            isPendingDelete ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Deleting" />
            ) : (
              <Checkbox
                aria-label={`Select ${landscape.prioritySearchPhrase}`}
                checked={isSelected}
                disabled={isBulkDeleting}
                onCheckedChange={(checked) => onSelect(landscape.id, !!checked)}
              />
            )
          ) : null}
        </TableCell>
        <TableCell className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
          {hasHistory && (
            <Button aria-label="Toggle version history" variant="ghost" size="icon" className="h-6 w-6" onClick={handleToggleExpand}>
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          )}
        </TableCell>
        <TableCell className="px-3 py-2 text-xs font-normal">
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-0.5">
              <span>{landscape.prioritySearchPhrase}</span>
              {isLandscapeFailed && (
                <span className="text-xs text-destructive font-medium">
                  Failed{failedLandscapeReason(latestInstance.status_info) ? `: ${failedLandscapeReason(latestInstance.status_info)}` : ""}
                </span>
              )}
              {isLandscapeStatusUnknown && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <HelpCircle className="h-3 w-3 shrink-0" />
                  Unknown
                </span>
              )}
              {!isLandscapeOpenable && !isLandscapeFailed && !isLandscapeStatusUnknown && latestInstance.status_info && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  {latestInstance.status_info}
                </span>
              )}
            </div>
            {hasHistory && (
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-primary/15 text-[10px]"
                onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}
              >
                {instances.length}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="px-3 py-2">
          <PresenceCompare hero={latestInstance.top4Hero} competitorAvg={latestInstance.top4CompetitorAvg} />
        </TableCell>
        <TableCell className="px-3 py-2">
          <PresenceCompare hero={latestInstance.page1Hero} competitorAvg={latestInstance.page1CompetitorAvg} />
        </TableCell>
        <TableCell className="px-3 py-2 text-xs text-muted-foreground text-center">
          {landscapeRunKeywordCountDisplay(latestInstance.status, latestInstance.keywordCount)}
        </TableCell>
        <TableCell className="px-3 py-2 text-xs text-muted-foreground">{latestInstance.date}</TableCell>
        <TableCell className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-0">
            {isLandscapeFailed ? (
              <span className="text-xs text-destructive font-medium px-2">Failed</span>
            ) : isLandscapeStatusUnknown ? (
              <span className="flex items-center gap-1 text-xs text-muted-foreground px-2">
                <HelpCircle className="h-4 w-4" />
                Unknown
              </span>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (isLandscapeOpenable) handleView(latestInstance.id); else toast({ title: `Status: ${runStatusLabel(latestInstance.status)}`, description: statusToastInfo }); }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isLandscapeOpenable ? "View landscape" : "View landscape status"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {!readOnly ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover z-50 rounded-[8px]">
                  <DropdownMenuItem onClick={() => heroAsin && selectedBrandId && createLandscape({ search_phrase: landscape.prioritySearchPhrase, hero_asin: heroAsin, brand_id: selectedBrandId })}>
                    <RefreshCw className="h-4 w-4 mr-2" />Refresh
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScheduleOpen(true)}>
                    <CalendarDays className="h-4 w-4 mr-2" />Schedule
                  </DropdownMenuItem>
                  {onCreateAbTest && (
                    <DropdownMenuItem onClick={() => onCreateAbTest(landscape.prioritySearchPhrase)}>
                      <FlaskConical className="h-4 w-4 mr-2" />Generate A/B Test
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteOpen(true)}>
                    <Trash2 className="h-4 w-4 mr-2" />Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {!readOnly ? (
              <ConfirmDeleteDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                title={`Delete "${landscape.prioritySearchPhrase}"?`}
                description="This will permanently delete this landscape and all its versions. This action cannot be undone."
                isPending={isPendingDelete}
                onConfirm={() => onDeleteLandscape(landscape.id)}
              />
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={readOnly ? 7 : 8} className="p-0">
            <NestedHistoryTable
              visibleHistory={visibleHistory}
              totalHistory={totalHistory}
              hasMore={hasMore}
              instanceCount={instances.length}
              landscapeName={landscape.prioritySearchPhrase}
              heroAsin={heroAsin}
              heroProductId={heroProductId}
              nestedSort={nestedSort}
              onSort={toggleNestedSort}
              onViewInstance={handleView}
              onToggleShowMore={() => setVisibleCount(hasMore ? instances.length : INITIAL_VISIBLE_VERSIONS)}
              readOnly={readOnly}
            />
          </TableCell>
        </TableRow>
      )}
      <ScheduleLandscapeModal open={scheduleOpen} onOpenChange={setScheduleOpen} landscapeId={Number(latestInstance.id)} landscapeName={landscape.prioritySearchPhrase} />
    </>
  );
}
